import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, Progress, Spin, Alert, Typography, Divider, Space, List, Button, Tag, Tooltip, DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
    TrophyOutlined,
    UserOutlined,
    BookOutlined,
    CheckCircleOutlined,
    BarChartOutlined,
    TeamOutlined,
    PercentageOutlined,
    ShopOutlined,
    ArrowLeftOutlined,
    ReloadOutlined,
} from '@ant-design/icons';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getExamStatistics } from '../../core/exam/examApi';
import { ExamStatisticsIndicator, ExamStatisticsSchool } from '../../core/exam/types';
import { formatNumber } from '../../core/utils/helpers';
import './style.scss';

const { Title, Text } = Typography;

const ExamsStatistic: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { examStatistics, isFetching, error } = useSelector((state:CombinedStates) => state.exam);
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const [selectedSchool, setSelectedSchool] = useState<ExamStatisticsSchool | null>(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    // Check if user has roles 1, 2, 3, or 4
    const canSelectDate = userData?.role_id && [1, 2, 3, 4].includes(userData.role_id);

    const fetchExamStatistics = () => {
        const payload: { gh: number; date?: string } = { gh: 1 };

        // Add date parameter if user can select date
        if (canSelectDate) {
            payload.date = selectedDate.format('YYYY-MM-DD');
        }

        dispatch(getExamStatistics(payload));
    };

    useEffect(() => {
        fetchExamStatistics();
    }, [dispatch, canSelectDate, selectedDate]);

    if (isFetching) {
        return (
            <Card title="Egzaminų statistika" className="exams-statistic-card">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px' }}>
                        <Text type="secondary">Kraunama egzaminų statistika...</Text>
                    </div>
                </div>
            </Card>
        );
    }

    if (!examStatistics) {
        return (
            <Card title="Egzaminų statistika" className="exams-statistic-card">
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    <p>Egzaminų statistika neprieinama</p>
                </div>
            </Card>
        );
    }

    const { overall, schools_summary, period, calculated_at, by_school } = examStatistics;

    const formatPercentage = (value: number): string => `${value.toFixed(1)}%`;

    const formatDate = (dateString: string): string => new Date(dateString).toLocaleString('lt-LT');

    const renderIndicator = (key: string, indicator: ExamStatisticsIndicator) => (
        <Col xs={24} sm={12} md={8} key={key}>
            <Card size="small">
                <Statistic
                    title={indicator.title}
                    value={formatNumber(indicator.value)}
                    suffix={indicator.unit}
                    valueStyle={{
                        color: indicator.value && Number(indicator.value) > 0 ? '#fdab01' : '#d9d9d9',
                    }} />
            </Card>
        </Col>
    );

    const renderOverallStatistics = () => (
        <Card
            title={(
                <Space>
                    <BarChartOutlined />
                    <span>Bendra egzaminų statistika</span>
                </Space>
            )}
            className="exams-statistic-card"
            extra={(

                <Row justify="space-between" gutter={16} align="middle">
                    <Col>
                        <Space>
                            {canSelectDate && (
                                <DatePicker
                                    value={selectedDate}
                                    onChange={(date) => { setSelectedDate(date || dayjs()); }}
                                    format="YYYY-MM-DD"
                                    placeholder="Pasirinkite datą"
                                    style={{ width: 150 }}
                                    allowClear={false}
                                    disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            )}
                            <Tooltip title="Atnaujinti duomenis">
                                <Button
                                    type="default"
                                    icon={<ReloadOutlined spin={isFetching} />}
                                    onClick={fetchExamStatistics}
                                    disabled={isFetching}
                                    size="small"
                                    style={{ backgroundColor: 'white', borderColor: '#d9d9d9' }} />
                            </Tooltip>
                        </Space>
                    </Col>
                    <Col style={{ textAlign: 'right' }}>
                        <Space direction="vertical" size="small">
                            <Text type="secondary">Laikotarpis: {period}</Text>
                            <Text type="secondary">
                                Atnaujinta: {formatDate(calculated_at)}
                            </Text>
                        </Space>
                    </Col>
                </Row>

            )}>
            {/* Overall Statistics */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Bendra statistika</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="Mokyklų skaičius"
                                value={overall.total_schools}
                                prefix={<TeamOutlined />}
                                valueStyle={{ color: '#fdab01' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="Mokyklos su egzaminų duomenimis"
                                value={overall.schools_with_exam_data}
                                prefix={<BookOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="Dalyvavo egzaminuose"
                                value={formatNumber(overall.total_students_taken_exams)}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#fdab01' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="Išlaikė egzaminus"
                                value={formatNumber(overall.total_students_passed_exams)}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="Išlaikymo procentas"
                                value={overall.average_pass_rate}
                                prefix={<PercentageOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card size="small">
                            <Statistic
                                title="STEAM dalyviai"
                                value={formatNumber(overall.steam_participation)}
                                prefix={<TrophyOutlined />}
                                valueStyle={{ color: '#fa8c16' }} />
                        </Card>
                    </Col>
                </Row>
            </div>

            <Divider />

            {/* Performance Metrics */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Veiklos rodikliai</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small">
                            <Statistic
                                title="Vidutinis balas"
                                value={overall.average_average_score.toFixed(2)}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ color: '#fdab01' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small">
                            <Statistic
                                title="Bendras egzaminų skaičius"
                                value={overall.total_exam_results}
                                prefix={<BookOutlined />}
                                valueStyle={{ color: '#722ed1' }} />
                        </Card>
                    </Col>
                </Row>
            </div>

            <Divider />

            {/* Key Indicators */}
            <div>
                <Title level={4}>Pagrindiniai rodikliai</Title>
                <Row gutter={[16, 16]}>
                    {Object.entries(overall.indicators)
                        .map(([key, indicator]) => renderIndicator(key, indicator as ExamStatisticsIndicator))}
                </Row>
            </div>
        </Card>
    );

    const renderSchoolList = () => (
        <Card
            title={(
                <Space>
                    <ShopOutlined />
                    <span>Mokyklų sąrašas</span>
                </Space>
            )}
            className="exams-statistic-card"
            style={{ height: 'fit-content' }}>
            <List
                size="small"
                dataSource={Object.values(by_school)}
                renderItem={(school) => (
                    <List.Item
                        style={{
                            padding: '8px 0',
                            cursor: 'pointer',
                            backgroundColor: selectedSchool?.school_id === school.school_id ? '#f0f0f0' : 'transparent',
                            borderRadius: '4px',
                            margin: '2px 0',
                        }}
                        onClick={() => setSelectedSchool(school)}>
                        <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {school.school_name}
                                    </Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        ID: {school.school_id}
                                    </Text>
                                </div>
                                <div style={{ textAlign: 'right', marginLeft: '8px' }}>
                                    {school.exam_results.length > 0 && (
                                        <Tag color="green">Turi duomenų</Tag>
                                    )}
                                    {school.exam_results.length === 0 && (
                                        <Tag color="orange">Nėra duomenų</Tag>
                                    )}
                                </div>
                            </div>
                            <div style={{ marginTop: '4px' }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Egzaminų: {school.exam_summary.total_exams}
                                </Text>
                            </div>
                        </div>
                    </List.Item>
                )} />
        </Card>
    );

    const renderSchoolDetails = (school: ExamStatisticsSchool) => (
        <Card
            title={(
                <Space>
                    <ShopOutlined />
                    <span>{school.school_name}</span>
                </Space>
            )}
            className="exams-statistic-card"
            extra={(
                <Space direction="vertical" size="small" style={{ textAlign: 'right' }}>
                    <Text>Laikotarpis: {period}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Atnaujinta: {formatDate(calculated_at)}
                    </Text>
                </Space>
            )}>
            {/* School Summary */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Mokyklos apžvalga</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Bendras egzaminų skaičius"
                                value={school.exam_summary.total_exams}
                                prefix={<BookOutlined />}
                                valueStyle={{ color: '#fdab01' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Dalyvavę mokiniai"
                                value={formatNumber(school.exam_summary.total_students_taken)}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Išlaikę mokiniai"
                                value={formatNumber(school.exam_summary.total_students_passed)}
                                prefix={<CheckCircleOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="STEAM egzaminai"
                                value={school.exam_summary.steam_exams}
                                prefix={<TrophyOutlined />}
                                valueStyle={{ color: '#fa8c16' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Mokiniai su 86-100 balais"
                                value={formatNumber(school.exam_summary.total_students_scored_86_100)}
                                prefix={<TrophyOutlined />}
                                valueStyle={{ color: '#fa8c16' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Šimtukai"
                                value={formatNumber(school.exam_summary.total_hundreds)}
                                prefix={<TrophyOutlined />}
                                valueStyle={{ color: '#fa8c16' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="STEAM mokiniai"
                                value={formatNumber(school.exam_summary.steam_students)}
                                prefix={<UserOutlined />}
                                valueStyle={{ color: '#fa8c16' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Egzaminų centrai"
                                value={school.exam_summary.exam_centers}
                                prefix={<ShopOutlined />}
                                valueStyle={{ color: '#722ed1' }} />
                        </Card>
                    </Col>
                </Row>
            </div>

            <Divider />

            {/* Performance Metrics */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={4}>Veiklos rodikliai</Title>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small">
                            <Statistic
                                title="Vidutinis išlaikymo procentas"
                                value={school.exam_summary.average_pass_rate}
                                prefix={<PercentageOutlined />}
                                valueStyle={{ color: '#52c41a' }} />
                            <Progress
                                percent={school.exam_summary.average_pass_rate}
                                strokeColor="#52c41a"
                                showInfo={false}
                                style={{ marginTop: '8px' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small">
                            <Statistic
                                title="Vidutinis balas"
                                value={school.exam_summary.average_average_score.toFixed(2)}
                                prefix={<BarChartOutlined />}
                                valueStyle={{ color: '#fdab01' }} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small">
                            <Statistic
                                title="Egzaminų centrai"
                                value={school.exam_summary.exam_centers}
                                prefix={<ShopOutlined />}
                                valueStyle={{ color: '#722ed1' }} />
                        </Card>
                    </Col>
                </Row>
            </div>

            <Divider />

            {/* Calculated Metrics */}
            {school.calculated_metrics && Object.keys(school.calculated_metrics).length > 0 && (
                <>
                    <div style={{ marginBottom: '24px' }}>
                        <Title level={4}>Apskaičiuoti rodikliai</Title>
                        <Row gutter={[16, 16]}>
                            {school.calculated_metrics.exam_participation_rate !== undefined && (
                                <Col xs={24} sm={12} md={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="Egzaminų dalyvavimo procentas"
                                            value={school.calculated_metrics.exam_participation_rate}
                                            prefix={<PercentageOutlined />}
                                            valueStyle={{ color: '#fdab01' }} />
                                    </Card>
                                </Col>
                            )}
                            {school.calculated_metrics.steam_participation_rate !== undefined && (
                                <Col xs={24} sm={12} md={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="STEAM dalyvavimo procentas"
                                            value={school.calculated_metrics.steam_participation_rate}
                                            prefix={<PercentageOutlined />}
                                            valueStyle={{ color: '#fa8c16' }} />
                                    </Card>
                                </Col>
                            )}
                            {school.calculated_metrics.average_exams_per_student !== undefined && (
                                <Col xs={24} sm={12} md={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="Vidutinis egzaminų skaičius mokiniui"
                                            value={school.calculated_metrics.average_exams_per_student}
                                            prefix={<BarChartOutlined />}
                                            valueStyle={{ color: '#722ed1' }} />
                                    </Card>
                                </Col>
                            )}
                            {school.calculated_metrics.overall_success_rate !== undefined && (
                                <Col xs={24} sm={12} md={6}>
                                    <Card size="small">
                                        <Statistic
                                            title="Bendras sėkmės procentas"
                                            value={school.calculated_metrics.overall_success_rate}
                                            prefix={<PercentageOutlined />}
                                            valueStyle={{ color: '#52c41a' }} />
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </div>
                    <Divider />
                </>
            )}

            {/* By Grade Level */}
            {school.exam_summary.by_grade_level && Object.keys(school.exam_summary.by_grade_level).length > 0 && (
                <>
                    <div style={{ marginBottom: '24px' }}>
                        <Title level={4}>Pagal klasių lygius</Title>
                        <Row gutter={[16, 16]}>
                            {Object.entries(school.exam_summary.by_grade_level).map(([grade, data]) => (
                                <Col xs={24} sm={12} md={8} key={grade}>
                                    <Card size="small">
                                        <Statistic
                                            title={`${grade} klasė`}
                                            value={data.count}
                                            suffix="egzaminai"
                                            prefix={<BookOutlined />}
                                            valueStyle={{ color: '#fdab01' }} />
                                        <div style={{ marginTop: '8px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Dalyvavo: {formatNumber(data.students_taken)} | Išlaikė: {formatNumber(data.students_passed)}
                                            </Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Išlaikymo procentas: {data.average_pass_rate}%
                                            </Text>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    <Divider />
                </>
            )}

            {/* By Exam Type */}
            {school.exam_summary.by_exam_type && Object.keys(school.exam_summary.by_exam_type).length > 0 && (
                <>
                    <div style={{ marginBottom: '24px' }}>
                        <Title level={4}>Pagal egzaminų tipus</Title>
                        <Row gutter={[16, 16]}>
                            {Object.entries(school.exam_summary.by_exam_type).map(([examType, data]) => (
                                <Col xs={24} sm={12} md={8} key={examType}>
                                    <Card size="small">
                                        <Statistic
                                            title={examType.toUpperCase()}
                                            value={data.count}
                                            suffix="egzaminai"
                                            prefix={<BookOutlined />}
                                            valueStyle={{ color: '#722ed1' }} />
                                        <div style={{ marginTop: '8px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Dalyvavo: {formatNumber(data.students_taken)} | Išlaikė: {formatNumber(data.students_passed)}
                                            </Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Išlaikymo procentas: {data.average_pass_rate}%
                                            </Text>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    <Divider />
                </>
            )}

            {/* Key Indicators */}
            <div>
                <Title level={4}>Pagrindiniai rodikliai</Title>
                <Row gutter={[16, 16]}>
                    {Object.entries(school.indicators)
                        .map(([key, indicator]) => renderIndicator(key, indicator as ExamStatisticsIndicator))}
                </Row>
            </div>
        </Card>
    );

    const renderEmptyState = () => (
        <Card
            title={(
                <Space>
                    <ShopOutlined />
                    <span>Pasirinkite mokyklą</span>
                </Space>
            )}
            className="exams-statistic-card">
            <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#999',
            }}>
                <ShopOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#999' }}>
                    Pasirinkite mokyklą iš sąrašo
                </Title>
                <Text type="secondary">
                    Kad pamatytumėte detalią mokyklos statistiką, pasirinkite mokyklą iš kairės sąrašo.
                </Text>
            </div>
        </Card>
    );

    return (
        <div className="exams-statistic-container">
            {renderOverallStatistics()}
            <div style={{ marginTop: '24px' }}>
                <Row gutter={24}>
                    <Col xs={24} lg={8} xl={6}>
                        {renderSchoolList()}
                    </Col>
                    <Col xs={24} lg={16} xl={18}>
                        {selectedSchool ? renderSchoolDetails(selectedSchool) : renderEmptyState()}
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default ExamsStatistic;
